import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SiteLayout from "../components/SiteLayout";
import { clearAuth, getAuthToken } from "../auth/auth";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://pelaj-forever2025-production.up.railway.app/weddingAccess";

export default function AdminGetAllGuestsPage() {
  const navigate = useNavigate();
  const token = useMemo(() => getAuthToken(), []);

  const [guestsLoading, setGuestsLoading] = useState(false);
  const [guestsError, setGuestsError] = useState("");
  const [guests, setGuests] = useState([]);

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const logout = () => {
    clearAuth();
    navigate("/admin/login", { replace: true });
  };

  useEffect(() => {
    if (!token) navigate("/admin/login", { replace: true });
  }, [token, navigate]);

  const fetchGuestsPage = async (p) => {
    setGuestsError("");
    setGuestsLoading(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/getAllGuests?page=${p}&size=${size}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const json = await res.json();

      if (!res.ok || json?.statusCode !== 200) {
        throw new Error(json?.description || "Failed to load guests");
      }

      const data = json?.data;
      setGuests(Array.isArray(data?.content) ? data.content : []);
      setTotalPages(Number(data?.totalPages || 0));
      setPage(Number(data?.page ?? p));
    } catch (e) {
      setGuestsError(e?.message || "Unable to load guests");
    } finally {
      setGuestsLoading(false);
    }
  };

  const goToPage = (p) => {
    setPage(p);
    fetchGuestsPage(p);
  };

  // ✅ uses your existing endpoint from screenshot:
  // GET /searchForGuest?phoneNumber=0703...
  const handleSearch = async () => {
    const q = phoneNumber.trim();
    if (!q) return;

    setGuestsError("");
    setGuestsLoading(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/searchForGuest?phoneNumber=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const json = await res.json();

      if (!res.ok || json?.statusCode !== 200) {
        throw new Error(json?.description || "Guest not found");
      }

      const guest = json?.data?.guestDetails;
      if (!guest) throw new Error("Guest not found");

      setIsSearching(true);
      setGuests([guest]);

      // Search mode: no pagination
      setTotalPages(1);
      setPage(0);
    } catch (e) {
      setGuestsError(e?.message || "Search failed");
      setGuests([]);
      setIsSearching(true); // still in search mode if they want to clear
      setTotalPages(1);
      setPage(0);
    } finally {
      setGuestsLoading(false);
    }
  };

  const clearSearch = () => {
    setPhoneNumber("");
    setIsSearching(false);
    setGuests([]);
    setTotalPages(0);
    setPage(0);
    fetchGuestsPage(0);
  };

  useEffect(() => {
    fetchGuestsPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SiteLayout>
      <div className="admin-page">
        <div className="admin-card">
          {/* Header */}
          <div className="admin-row" style={{ alignItems: "center" }}>
            <div>
              <h2 className="admin-title" style={{ marginBottom: 4 }}>
                All Guests
              </h2>
              <p className="admin-subtitle" style={{ margin: 0 }}>
                Search by phone number or browse the guest list
              </p>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="admin-btn outline"
                onClick={() => navigate("/admin/dashboard")}
              >
                Back to Dashboard
              </button>
              <button className="admin-btn outline" onClick={logout}>
                Logout
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="admin-form" style={{ marginTop: 14 }}>
            <label className="admin-label">
              Search by Phone Number
              <input
                className="admin-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. 07033830415"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </label>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="admin-btn primary"
                type="button"
                onClick={handleSearch}
                disabled={guestsLoading || !phoneNumber.trim()}
              >
                Search
              </button>

              {(isSearching || phoneNumber.trim()) && (
                <button
                  className="admin-btn outline"
                  type="button"
                  onClick={clearSearch}
                  disabled={guestsLoading}
                >
                  Clear Search
                </button>
              )}
            </div>
          </div>

          {guestsError && <div className="admin-error">{guestsError}</div>}

          {/* Table */}
          <div className="dash-table-wrap" style={{ marginTop: 14 }}>
            <table className="dash-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Checked In</th>
                  <th>Band Code</th>
                  <th>QR Code</th>
                </tr>
              </thead>

              <tbody>
                {guestsLoading ? (
                  <tr>
                    <td colSpan={5} style={{ opacity: 0.8 }}>
                      Loading...
                    </td>
                  </tr>
                ) : guests.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ opacity: 0.8 }}>
                      No guests found.
                    </td>
                  </tr>
                ) : (
                  guests.map((g) => (
                    <tr key={g.id ?? g.qrCode}>
                      <td>{g.name}</td>
                      <td>{g.phoneNumber}</td>
                      <td>{g.hasCheckedIn ? "Yes" : "No"}</td>
                      <td>{g.partyBandCode || "-"}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {g.qrCode}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination (hidden during search mode) */}
          {!isSearching && (
            <div className="dash-pagination" style={{ marginTop: 14 }}>
              <button
                className="admin-btn outline"
                onClick={() => goToPage(Math.max(0, page - 1))}
                disabled={guestsLoading || page <= 0}
              >
                Prev
              </button>

              <div className="dash-pagination-meta">
                Page <b>{page + 1}</b> of <b>{totalPages || 1}</b>
              </div>

              <button
                className="admin-btn outline"
                onClick={() => goToPage(Math.min(totalPages - 1, page + 1))}
                disabled={
                  guestsLoading || totalPages === 0 || page >= totalPages - 1
                }
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}
